// Regex cheatsheet modal — ported from the original Shiny PMC search app so the
// reference and EV-flavoured examples stay identical across both tools.

interface Item {
  t: string          // the token, e.g. \d
  d: string          // short label
  ex?: string        // example pattern
  m?: string         // what the example matches
}

interface Section {
  title: string
  items: Item[]
}

const LEFT: Section[] = [
  {
    title: 'Anchors & Boundaries',
    items: [
      { t: '^', d: 'Start of string/line.', ex: '^Exosome', m: "matches lines starting with 'Exosome'." },
      { t: '$', d: 'End of string/line.', ex: 'marker$', m: "matches lines ending with 'marker'." },
      { t: '\\b', d: 'Word boundary.', ex: '\\bEVs\\b', m: "matches 'EVs' but not 'EVsAssociated'." },
      { t: '\\B', d: 'Not a word boundary.', ex: '\\Bintra\\B', m: "matches 'intra' within 'intracellular'." },
    ],
  },
  {
    title: 'Character Classes (Shorthand)',
    items: [
      { t: '.', d: 'Any character (except newline).', ex: 'CD.1', m: "matches 'CD81', 'CD61', etc." },
      { t: '\\d', d: 'Any digit [0-9].', ex: 'CD\\d{2}', m: "matches 'CD' followed by 2 digits (e.g., 'CD63')." },
      { t: '\\D', d: 'Any non-digit.', ex: '\\D\\d', m: "matches a non-digit then a digit (e.g., ' EV1')." },
      { t: '\\w', d: 'Any word character [a-zA-Z0-9_].', ex: '\\w+some', m: "matches 'exosome', 'liposome'." },
      { t: '\\W', d: 'Any non-word character.', ex: 'CD63\\W', m: "matches 'CD63' followed by space, comma, etc." },
      { t: '\\s', d: 'Any whitespace character.', ex: 'extracellular\\svesicle', m: "matches 'extracellular vesicle'." },
      { t: '\\S', d: 'Any non-whitespace character.', ex: '100\\Snm', m: "matches '100nm' but not '100 nm'." },
    ],
  },
  {
    title: 'Character Sets',
    items: [
      { t: '[abc]', d: "Matches 'a', 'b', OR 'c'.", ex: '[Ee]xosome', m: "matches 'Exosome' or 'exosome'." },
      { t: '[^abc]', d: "Matches any character EXCEPT 'a', 'b', 'c'.", ex: 'EV[^s]', m: "matches 'EVa', 'EVb' but not 'EVs'." },
      { t: '[a-z]', d: 'Matches any lowercase letter.', ex: 'marker-[a-z]', m: "matches 'marker-a', 'marker-b' etc." },
      { t: '[A-Z]', d: 'Matches any uppercase letter.', ex: 'Protein[A-Z]', m: "matches 'ProteinA', 'ProteinZ'." },
      { t: '[0-9]', d: 'Matches any digit.', ex: 'CD[6-8][1-9]', m: "matches 'CD61'-'CD69', 'CD71'-'CD79', 'CD81'-'CD89'." },
    ],
  },
]

const RIGHT: Section[] = [
  {
    title: 'Quantifiers (How Many?)',
    items: [
      { t: '*', d: '0 or more times.', ex: 'exo.*marker', m: "matches 'exomarker', 'exosome marker'." },
      { t: '+', d: '1 or more times.', ex: 'CD\\d+', m: "matches 'CD' followed by 1+ digits ('CD9', 'CD63')." },
      { t: '?', d: '0 or 1 time.', ex: 'vesicles?', m: "matches 'vesicle' or 'vesicles'." },
      { t: '{n}', d: 'Exactly n times.', ex: '\\d{3}nm', m: "matches '100nm', '050nm' (exactly 3 digits)." },
      { t: '{n,}', d: 'n or more times.', ex: '\\d{2,} cells', m: "matches '10 cells', '1000 cells'." },
      { t: '{n,m}', d: 'Between n and m times.', ex: '\\d{2,4}\\s?kDa', m: "matches '10 kDa', '150 kDa', '1200 kDa'." },
    ],
  },
  {
    title: 'Alternation (OR)',
    items: [
      { t: 'cat|dog', d: "Matches 'cat' OR 'dog'.", ex: 'exosome|microvesicle', m: 'matches either term.' },
    ],
  },
  {
    title: 'Grouping and Capturing',
    items: [
      { t: '(...)', d: 'Groups expressions & captures the match.', ex: '(CD\\d+)', m: 'captures the specific CD marker found.' },
    ],
  },
  {
    title: 'Escaping & Special Characters',
    items: [
      { t: '\\', d: 'Escapes the next character.', ex: 'p\\s?<\\s?0\\.05', m: "matches 'p<0.05', 'p < 0.05' by escaping the '.'." },
      { t: '\\n', d: 'Newline character.' },
      { t: '\\t', d: 'Tab character.' },
    ],
  },
]

const code = 'px-1 py-0.5 bg-gray-100 rounded text-[12px] font-mono text-purple-700'

function SectionBlock({ section }: { section: Section }) {
  return (
    <div className="mb-4">
      <h4 className="font-semibold text-sm text-gray-800 mb-1">{section.title}</h4>
      <ul className="space-y-1">
        {section.items.map((it, i) => (
          <li key={i} className="text-[13px] text-gray-700 leading-relaxed">
            <code className={code}>{it.t}</code> : {it.d}
            {it.ex && (
              <> <em className="text-gray-500">Ex: </em><code className={code}>{it.ex}</code> {it.m}</>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default function RegexCheatsheet({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">📖 Regex Cheatsheet with Examples</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>{LEFT.map((s, i) => <SectionBlock key={i} section={s} />)}</div>
            <div>{RIGHT.map((s, i) => <SectionBlock key={i} section={s} />)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
